import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import PlusIcon from '@mui/icons-material/Add'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { FormContext } from '../Contexts'
import { SelectRegistered, TextFieldRegistered } from '../Components'
import { FormHandleSubmit, GetSchemaValidation } from '../types'

import { SkillsValidationSchema } from './ValidationSchemas'
import { languages, skills } from '../../../data'

export const SkillsStep = ({
  id,
  data,
  setActiveForm,
}: {
  id: string
  data?: GetSchemaValidation<typeof SkillsValidationSchema>
  setActiveForm: (handleSubmit: () => FormHandleSubmit<GetSchemaValidation<typeof SkillsValidationSchema>>) => void
}) => {
  const { control, handleSubmit, register, setValue, watch, formState, getValues } = useForm({
    resolver: yupResolver(SkillsValidationSchema),
    defaultValues: data,
  })
  const [course, setCourse] = React.useState<string>('')
  const selectedLanguages = watch('languages') || []
  const selectedSkills = watch('skills') || []

  useEffect(() => {
    setActiveForm(() => ({ formId: id, getData: getValues, submit: handleSubmit }))
  }, [getValues, handleSubmit, id, setActiveForm])

  const handleCompletedCourseDelete = (index: number) => {
    const courses = getValues('completedCourses')

    courses?.splice(index, 1)

    setValue('completedCourses', courses)
  }

  const handleCompletedCourseAdd = () => {
    if (!course) return

    setValue('completedCourses', [...(getValues('completedCourses') || []), course])
    setCourse('')
  }

  return (
    <FormContext.Provider value={{ control, handleSubmit, register, setValue, watch, formState }}>
      <Grid container spacing={2} rowSpacing={2}>
        <Grid item xs={12}>
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="alfabetizado.status"
              label="Alfabetizado"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="primaria.status"
              label="Primaria"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered label="Institución" name="primaria.institucion" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="secundaria.status"
              label="Secundaria"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered label="Institución" name="secundaria.institucion" />
          </Grid>
        </Grid>
        <Grid container item xs={12} justifyContent="flex-end">
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="terciaria.status"
              label="Terciaria"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered label="Institución" name="terciaria.institucion" />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered name="terciaria.career" label="Carrera Técnica y/o Universitaria" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="posGraduacion.status"
              label="Pós Graduación"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered label="Institución" name="posGraduacion.institucion" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="mestrado.status"
              label="Mestrado"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered label="Institución" name="mestrado.institucion" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={6} md={6}>
            <SelectRegistered
              name="doctorado.status"
              label="Doctorado"
              options={[{ label: 'Incompleta' }, { label: 'Completa' }]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <TextFieldRegistered label="Institución" name="doctorado.institucion" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={12} md={12}>
            <TextFieldRegistered label="Habilidades Personales" multiline rows={5} name="personalSkills" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={12} md={12}>
            <TextFieldRegistered label="Resumen de Actividades Ejercidas" multiline rows={5} name="activitiesPerformed" />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} sm={8} md={8}>
            <TextField
              label="Cursos Realizados"
              name="course"
              variant="standard"
              value={course}
              onChange={(event) => setCourse(event.target.value)}
              fullWidth
              error={!!formState?.errors?.completedCourses?.message?.toString()}
            />
          </Grid>
          <Grid
            container
            item
            xs={12}
            sm={4}
            md={4}
            direction="column"
            justifyContent="end"
            alignItems={'start'}
            paddingLeft={10}
          >
            <Button
              variant="outlined"
              type="button"
              color="primary"
              onClick={() => handleCompletedCourseAdd()}
              startIcon={<PlusIcon />}
              fullWidth
            >
              Agregar
            </Button>
          </Grid>
          <Grid item xs={8} sm={8} md={8}>
            <Typography variant="inherit" color="red" align="left" style={{ marginTop: 10 }}>
              {formState?.errors?.completedCourses?.message?.toString()}
            </Typography>
          </Grid>

          <Grid
            container
            item
            xs={12}
            sm={12}
            md={12}
            direction="row"
            justifyContent="start"
            alignItems={'start'}
            style={{ marginTop: 10 }}
          >
            {watch('completedCourses')?.map((course, index) => (
              <Chip key={index} label={course} variant="outlined" onDelete={() => handleCompletedCourseDelete(index)} />
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12} textAlign="left">
          <FormControl component="fieldset">
            <FormLabel component="legend">Idiomas</FormLabel>
            <FormGroup row>
              {languages.map((language) => (
                <FormControlLabel
                  key={`language-${language}`}
                  value={language}
                  control={<Checkbox />}
                  label={language}
                  checked={selectedLanguages?.includes?.(language)}
                  {...register('languages')}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} textAlign="left">
          <FormControl component="fieldset">
            <FormLabel component="legend">Conhecimentos Tecnológicos</FormLabel>
            <FormGroup row>
              {skills.map((skill) => (
                <FormControlLabel
                  key={`skill-${skill}`}
                  value={skill}
                  control={<Checkbox />}
                  label={skill}
                  checked={selectedSkills?.includes?.(skill)}
                  {...register('skills')}
                />
              ))}
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
    </FormContext.Provider>
  )
}
